import { ChevronDown, LogOut } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import { Link, useLocation } from 'react-router'

import { isBillingEnabled } from '@/api/config'
import { SidebarUserRow } from '@/components/sidebar-user-row'
import { Breadcrumb } from '@/components/ui/breadcrumb'
import {
  Sidebar,
  SidebarAutoclose,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger
} from '@/components/ui/sidebar'
import { SidebarCountBadge } from '@/components/ui/sidebar-count-badge'
import { TooltipProvider } from '@/components/ui/tooltip'
import {
  type NavChild,
  type NavItem,
  ActivationFailedBanner,
  badgeFor,
  BillingAlertBanner,
  CapabilitiesUpdateBanner,
  findNavTrail,
  getNavBadges,
  getSettingsNav,
  isNavGroupOpen,
  markNavGroupOpen,
  NAV_GROUPS,
  SidebarQuickNav,
  toggleNavGroup,
  useNavGroupVersion,
  useSidebarScroll,
  visibleNavGroups
} from '@/domains/app-shell'
import { useUser } from '@/domains/auth'
import { SidebarCompanyHeader } from '@/domains/companies'
import { InstallSidebarRow } from '@/domains/install'
import { useSetupState } from '@/domains/setup'

export function AppShell({
  children,
  title
}: {
  children: ReactNode
  title?: ReactNode
}) {
  const { user, logout } = useUser()
  const { setupState } = useSetupState()
  const location = useLocation()
  const sidebarScrollRef = useSidebarScroll()
  /** super nav only surfaces in Super scope. acting as a company they own
   * shows the normal owner nav. */
  const inSuperScope = (user?.isSuper ?? false) && setupState?.scope === 'super'

  /* open/closed groups live in a module store, not state: this shell unmounts
   * on every navigation, and a group must stay open until the user closes it.
   * subscribing here re-renders the sidebar when one is toggled. */
  useNavGroupVersion()

  /** clicking a parent row toggles its group, and navigation happens either
   * way. generic across every grouping, any parent with children gets it.
   */
  function handleParentClick(item: NavItem) {
    if (!item.children?.length) return
    toggleNavGroup(item.href, isItemActive(item))
  }

  function isActive(href: string) {
    return (
      location.pathname === href || location.pathname.startsWith(`${href}/`)
    )
  }

  /** a parent stays active anywhere under it, including children that do not
   *  share its path prefix. a group whose href is its first child (/settings)
   *  can have siblings the prefix check alone would miss, and the group would
   *  collapse on them.
   */
  function isItemActive(item: NavItem) {
    return (
      isActive(item.href) ||
      (item.children?.some(child => isActive(child.href)) ?? false)
    )
  }

  /** a child is "active" only on exact match. parent stays active for any
   * descendant path (handled by isActive's prefix check) so the group
   * stays highlighted while the user is anywhere under it.
   */
  function isExactActive(href: string) {
    return location.pathname === href
  }

  const isSupplier = setupState?.isSupplier ?? false

  /** settings pages hang off the sidebar rather than a second nav inside the
   * page, so Settings is the one nav item whose children are computed: they
   * depend on the company's roles. flattened because the sidebar sub-list has
   * no group headings.
   */
  const settingsChildren: NavChild[] = setupState
    ? getSettingsNav(setupState).flatMap(group =>
        group.items.map(item => ({
          label: item.label,
          href: item.href,
          icon: item.icon
        }))
      )
    : []

  const visibleGroups = visibleNavGroups(NAV_GROUPS, {
    inSuperScope,
    isSupplier,
    billingEnabled: isBillingEnabled(),
    settingsChildren
  })

  /* a group you land in without clicking it (the post-login redirect, the
   * palette, a pasted url) is only open by inference, so navigating anywhere
   * else would collapse it under you. pinning it on arrival leaves the click on
   * its own row as the one thing that closes it. no dep array: this is
   * idempotent and has to re-run whenever the visible set changes. */
  useEffect(() => {
    for (const group of visibleGroups) {
      for (const item of group.items) {
        if (item.children?.length && isItemActive(item)) {
          markNavGroupOpen(item.href)
        }
      }
    }
  })

  /* a grouped page reads "Supplier Network > All Suppliers" off the nav, so no
   * page restates its own parent. detail routes never match and fall back to
   * whatever title they passed. */
  const trail = findNavTrail(visibleGroups, location.pathname)
  const heading = trail ? <Breadcrumb items={trail} /> : title

  /* open tasks, not notifications: no dismiss, each clears when its work is
   * done. a parent rolls up whatever its children carry. */
  const badges = getNavBadges(setupState, isBillingEnabled())

  return (
    <TooltipProvider delayDuration={0}>
      <SidebarProvider>
        <SidebarAutoclose />
        <Sidebar collapsible='icon'>
          <SidebarHeader>
            <SidebarCompanyHeader homeHref='/' />
            <SidebarQuickNav />
          </SidebarHeader>

          <SidebarContent ref={sidebarScrollRef}>
            {visibleGroups.map(group => (
              <SidebarGroup key={group.label}>
                <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map(item => {
                      const parentActive = isItemActive(item)
                      const hasChildren = Boolean(item.children?.length)
                      /** when a child route exactly matches, the child carries
                       * the highlight, the parent row stays un-highlighted so
                       * only the active leaf reads as selected. */
                      const childExactActive =
                        item.children?.some(child =>
                          isExactActive(child.href)
                        ) ?? false
                      const expanded =
                        hasChildren && isNavGroupOpen(item.href, parentActive)
                      return (
                        <SidebarMenuItem key={item.href}>
                          <SidebarMenuButton
                            asChild
                            /* a collapsed group hides the active child, so the
                             * parent takes the highlight back or nothing on
                             * screen reads as selected. */
                            isActive={
                              (parentActive &&
                                (!childExactActive || !expanded)) ||
                              undefined
                            }
                          >
                            <Link
                              to={item.href}
                              onClick={() => handleParentClick(item)}
                            >
                              <span className='relative inline-flex'>
                                <item.icon />
                                <SidebarCountBadge
                                  count={badgeFor(badges, item)}
                                  variant='dot'
                                />
                              </span>
                              <span className='flex-1'>{item.label}</span>
                              <SidebarCountBadge
                                count={badgeFor(badges, item)}
                                variant='pill'
                              />
                              {hasChildren ? (
                                <ChevronDown
                                  className={`size-4 text-sidebar-foreground/60 transition-transform group-data-[collapsible=icon]:hidden ${
                                    expanded ? 'rotate-180' : ''
                                  }`}
                                />
                              ) : null}
                            </Link>
                          </SidebarMenuButton>
                          {item.children && expanded ? (
                            <SidebarMenuSub>
                              {item.children.map(child => (
                                <SidebarMenuSubItem key={child.href}>
                                  <SidebarMenuSubButton
                                    asChild
                                    isActive={
                                      isExactActive(child.href) || undefined
                                    }
                                  >
                                    <Link to={child.href}>
                                      <child.icon />
                                      <span className='flex-1'>
                                        {child.label}
                                      </span>
                                      <SidebarCountBadge
                                        count={badges[child.href] ?? 0}
                                        variant='pill'
                                      />
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          ) : null}
                        </SidebarMenuItem>
                      )
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}

            <SidebarGroup className='mt-auto'>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton onClick={() => void logout()}>
                      <LogOut />
                      <span>Logout</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter>
            <InstallSidebarRow />
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarUserRow name={user?.name} email={user?.email} />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>

          <SidebarRail />
        </Sidebar>

        <SidebarInset>
          <header className='sticky top-0 z-20 flex min-h-14 items-center gap-2 border-b border-border bg-page px-4 pt-[env(safe-area-inset-top)]'>
            <SidebarTrigger className='-ml-1' />
            <span className='min-w-0 text-sm font-medium text-ink'>
              {heading}
            </span>
          </header>
          <main className='flex w-full flex-1 flex-col p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6'>
            <div className='mb-2 flex flex-col gap-2 empty:hidden sm:mb-4 sm:gap-4'>
              {setupState?.supplier?.needsCapabilitiesUpdate && (
                <CapabilitiesUpdateBanner userId={user?.id ?? null} />
              )}
              {setupState?.entitlements && (
                <BillingAlertBanner
                  entitlements={setupState.entitlements}
                  userId={user?.id ?? null}
                />
              )}
              {setupState?.entitlements && (
                <ActivationFailedBanner
                  entitlements={setupState.entitlements}
                  userId={user?.id ?? null}
                />
              )}
            </div>
            {children}
          </main>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  )
}
